/**
 * Pure formatting for the admin "Glitimė" tab — kept out of Admin.tsx so the
 * mapping from raw surface/source-kind strings to a readable subtitle can be
 * tested without a component harness.
 */

/** Short label for a `glitime_lines.surface` value; unknown surfaces show as-is. */
const SURFACE_LABEL: Readonly<Record<string, string>> = {
  greeting: 'Pasisveikinimas',
  presence_remark: 'Pastaba',
  log_reaction: 'Reakcija į įrašą',
  'push.evening': 'Push (vakaras)',
  'push.morning_brief': 'Push (rytas)',
  'push.weigh_in': 'Push (svėrimasis)',
  wrapped_learned: 'Wrapped',
  chat_opener: 'Pokalbio pradžia',
}

export function surfaceLabel(surface: string): string {
  return SURFACE_LABEL[surface] ?? surface
}

/**
 * "iš pokalbio 08-21" for a chat/transcript source, "iš atminties (taste)
 * 08-19" for a `memory:<type>` source — `sourceDate` is an ISO date
 * ("YYYY-MM-DD"), sliced rather than parsed so no timezone can shift it.
 */
export function describeSource(sourceKind: string, sourceDate: string): string {
  const compactDate = sourceDate.slice(5)
  if (sourceKind === 'chat' || sourceKind === 'transcript') return `iš pokalbio ${compactDate}`
  if (sourceKind.startsWith('memory:')) {
    const memoryType = sourceKind.slice('memory:'.length)
    return `iš atminties (${memoryType}) ${compactDate}`
  }
  return `${sourceKind} ${compactDate}`
}

/** Whole days between an ISO timestamp and now (never negative). */
export function ageDays(createdAt: string, now: number = Date.now()): number {
  const ms = now - new Date(createdAt).getTime()
  return Math.max(0, Math.floor(ms / 86_400_000))
}

export interface AdminLine {
  id: string
  userName: string
  surface: string
  sourceKind: string
  sourceDate: string
  createdAt: string
  expiresAt: string
  title: string | null
  text: string
}

/** The row subtitle: who it's for, what it is, where it came from, how old. */
export function describeLine(line: AdminLine, now: number = Date.now()): string {
  const age = ageDays(line.createdAt, now)
  const ageLabel = age === 0 ? 'šiandien' : `prieš ${age} d.`
  return [
    line.userName,
    surfaceLabel(line.surface),
    describeSource(line.sourceKind, line.sourceDate),
    ageLabel,
  ].join(' · ')
}
